import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_bootstrap5.min.css';
import api from '../services/api';
import '../styles/auth.css';
import '../styles/home.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';

const GerenciadorAprovacoes = () => {
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [estados, setEstados] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [escolas, setEscolas] = useState([]);
  const [regionais, setRegionais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ tipo: '', texto: '' });
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Estados para atualização de perfil
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [nomeBusca, setNomeBusca] = useState('');
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [usuarioEncontrado, setUsuarioEncontrado] = useState(null);
  const [novoPerfilId, setNovoPerfilId] = useState('');
  const [novoUf, setNovoUf] = useState('');
  const [novoIdMunicipio, setNovoIdMunicipio] = useState('');
  const [novoIdRegional, setNovoIdRegional] = useState('');
  const [novoIdEscola, setNovoIdEscola] = useState('');
  const [municipiosFiltradosUpdate, setMunicipiosFiltradosUpdate] = useState([]);
  const [escolasFiltradas, setEscolasFiltradas] = useState([]);
  const [municipioSearchUpdate, setMunicipioSearchUpdate] = useState('');
  const [escolaSearch, setEscolaSearch] = useState('');

  // Estados para modal de aprovação com permissões
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [usuarioParaAprovar, setUsuarioParaAprovar] = useState(null);
  const [aprovacaoModoConsulta, setAprovacaoModoConsulta] = useState(false);
  const [aprovacaoAcessoSistema, setAprovacaoAcessoSistema] = useState(true);
  const [aprovacaoCriarQuestionarios, setAprovacaoCriarQuestionarios] = useState(false);
  const [aprovacaoAcessoResultados, setAprovacaoAcessoResultados] = useState(false);
  const [aprovacaoAcessoBDados, setAprovacaoAcessoBDados] = useState(true);
  const [aprovacaoEditarTabelas, setAprovacaoEditarTabelas] = useState(false);
  const [aprovacaoCriaAcoes, setAprovacaoCriaAcoes] = useState(false);
  const [isTableBuilt, setIsTableBuilt] = useState(false);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const formatCpf = (cpf) => {
    if (!cpf) return '';
    const cleanedCpf = cpf.replace(/\D/g, '');
    return cleanedCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMessage({ tipo, texto });
    setTimeout(() => setMessage({ tipo: '', texto: '' }), 5000);
  }, []);

  const fetchData = useCallback(async (showMessage = false) => {
    setLoading(true);
    try {
      const [usersRes, profilesRes, estadosRes, municipiosRes, escolasRes, regionaisRes] = await Promise.all([
        api.get('/usuario/pending-approvals'),
        api.get('/perfis'),
        api.get('/estado'),
        api.get('/municipio'),
        api.get('/escola'),
        api.get('/regional')
      ]);

      const users = usersRes.data.data || [];
      setPendingUsers(users.map(u => {
        const isAdmin = parseInt(u.id_perfil) === 1;
        return {
          ...u, 
          modo_consulta: false,
          acessa_sistema: true,
          cria_questionario: isAdmin,
          acessa_resultados: isAdmin,
          acessa_bdados: true,
          edita_tabela_tipo: isAdmin,
          cria_acoes: isAdmin
        };
      }));
      setProfiles(profilesRes.data.data || []);
      setEstados(estadosRes.data.data || []);
      setMunicipios(municipiosRes.data.data || []);
      setEscolas(escolasRes.data.data || []);
      setRegionais(regionaisRes.data.data || []);
      
      setLastUpdate(new Date());

      if (showMessage) {
        mostrarMensagem('success', `${users.length} usuários pendentes carregados.`);
      }
    } catch (err) {
      mostrarMensagem('error', 'Falha ao carregar dados. Verifique sua permissão e conexão.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = useCallback((user) => {
    setUsuarioParaAprovar(user);
    setAprovacaoModoConsulta(!!user.modo_consulta);
    setAprovacaoAcessoSistema(!!user.acessa_sistema);
    setAprovacaoCriarQuestionarios(!!user.cria_questionario);
    setAprovacaoAcessoResultados(!!user.acessa_resultados);
    setAprovacaoAcessoBDados(!!user.acessa_bdados);
    setAprovacaoEditarTabelas(!!user.edita_tabela_tipo);
    setAprovacaoCriaAcoes(!!user.cria_acoes);
    setShowApproveModal(true);
  }, []);

  const handleReject = useCallback(async (user) => {
    if (window.confirm(`Tem certeza que deseja REPROVAR e EXCLUIR o usuário "${user.nome_completo}"? A ação não pode ser desfeita.`)) {
      try {
        await api.delete(`/usuario/${user.id_usuario}`);
        mostrarMensagem('success', "Usuário reprovado e excluído com sucesso!");
        fetchData();
      } catch (err) {
        mostrarMensagem('error', err.response?.data?.message || 'Ocorreu um erro ao reprovar o usuário.');
      }
    }
  }, [fetchData, mostrarMensagem]);

  const confirmarAprovacao = useCallback(async () => {
    if (!usuarioParaAprovar) return;

    try {
      const payload = {
        modo_consulta: aprovacaoModoConsulta,
        acessa_sistema: aprovacaoAcessoSistema,
        cria_questionario: aprovacaoCriarQuestionarios,
        acessa_resultados: aprovacaoAcessoResultados,
        acessa_bdados: aprovacaoAcessoBDados,
        edita_tabela_tipo: aprovacaoEditarTabelas,
        cria_acoes: aprovacaoCriaAcoes
      };
      await api.patch(`/usuario/${usuarioParaAprovar.id_usuario}/approve`, payload);
      mostrarMensagem('success', `Usuário "${usuarioParaAprovar.nome_completo}" aprovado com sucesso!`);
      setShowApproveModal(false);
      setUsuarioParaAprovar(null);
      fetchData();
    } catch (err) {
      mostrarMensagem('error', err.response?.data?.message || 'Ocorreu um erro ao aprovar o usuário.');
    }
  }, [usuarioParaAprovar, aprovacaoModoConsulta, aprovacaoAcessoSistema, aprovacaoCriarQuestionarios, aprovacaoAcessoResultados, aprovacaoAcessoBDados, aprovacaoEditarTabelas, aprovacaoCriaAcoes, fetchData, mostrarMensagem]);

  const renderScopeDisplay = useCallback((user) => {
    const selectedProfile = profiles.find(p => p.id_perfil === user.id_perfil);
    if (!selectedProfile) return "Perfil não encontrado";
    const { nivel_hierarquia, nome_perfil } = selectedProfile;

    switch (nivel_hierarquia) {
      case 2:
        const estado = estados.find(e => e.id_sigla_est === user.uf);
        return estado ? estado.nome_estado : user.uf;
      case 3:
        const regional = regionais.find(r => r.id_regional === user.id_regional);
        return regional ? regional.nome_regional : `ID Regional: ${user.id_regional}`;
      case 4:
        if (nome_perfil.toLowerCase().includes('municipal')) {
          const municipio = municipios.find(m => m.codigo_ibge === user.codigo_ibge7);
          return municipio ? municipio.nome_municipio : 'Município não encontrado';
        }
        if (nome_perfil.toLowerCase().includes('escolar')) {
          const escola = escolas.find(e => e.codigo_inep === user.codigo_inep);
          return escola ? escola.nome_escola : `ID Escola: ${user.codigo_inep}`;
        }
        return "Não definido";
      default:
        return "Nível Nacional";
    }
  }, [profiles, estados, regionais, municipios, escolas]);

  const selecionarUsuario = useCallback((usuario) => {
    setUsuarioEncontrado(usuario);
    setNomeBusca(usuario.nome_completo);
    setNovoPerfilId(usuario.id_perfil?.toString() || '');
    setNovoUf(usuario.uf || '');
    setNovoIdRegional(usuario.id_regional?.toString() || '');
    setNovoIdEscola(usuario.codigo_inep || '');

    // Para perfis 3 (Gestor Municipal) e 4 (Gestor Escolar), o codigo_ibge7 armazenado
    // na tabela é o próprio id_municipio (mesma lógica do cadastro/register).
    const idPerfilNum = parseInt(usuario.id_perfil);
    const municipioId =
      (idPerfilNum === 3 || idPerfilNum === 4)
        ? (usuario.codigo_ibge7?.toString() || '')
        : '';
    setNovoIdMunicipio(municipioId);

    // Buscar o nome do município na lista completa para exibir no campo de busca
    if (municipioId) {
      const municipio = municipios.find(
        m => m.id_municipio?.toString() === municipioId
      );
      if (municipio) {
        setMunicipioSearchUpdate(municipio.nome_municipio);
      } else {
        setMunicipioSearchUpdate('');
      }
    } else {
      setMunicipioSearchUpdate('');
    }

    // Buscar o nome da escola se existir
    if (usuario.codigo_inep) {
      const escola = escolas.find(e => e.codigo_inep === usuario.codigo_inep);
      if (escola) {
        setEscolaSearch(escola.nome_escola);
      } else {
        setEscolaSearch('');
      }
    } else {
      setEscolaSearch('');
    }

    setUsuariosFiltrados([]);
  }, [municipios, escolas]);

  const getColunas = useMemo(() => {
    const currentUserStr = localStorage.getItem('user');
    const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
    const isAdmin = currentUser && currentUser.id_perfil === 1;

    const actionColumns = isAdmin ? [
      { 
        title: "Aprovar", 
        field: "aprovar",
        formatter: () => `<button class="btn-approve"><i class="fas fa-check"></i></button>`, 
        width: 100, 
        hozAlign: "center", 
        cellClick: (e, cell) => handleApprove(cell.getRow().getData()),
        headerSort: false
      },
      { 
        title: "Reprovar", 
        field: "reprovar",
        formatter: () => `<button class="btn-delete"><i class="fas fa-trash"></i></button>`, 
        width: 100, 
        hozAlign: "center", 
        cellClick: (e, cell) => handleReject(cell.getRow().getData()),
        headerSort: false
      }
    ] : [];

    return [
      ...actionColumns,
      { title: 'Nome completo', field: 'nome_completo', minWidth: 200, headerFilter: "input", headerFilterPlaceholder: "Filtrar..." },
      { title: 'Email', field: 'email', minWidth: 200, headerFilter: "input", headerFilterPlaceholder: "Filtrar..." },
      { title: 'CPF', field: 'cpf', width: 150, formatter: (cell) => formatCpf(cell.getValue()), headerFilter: "input", headerFilterPlaceholder: "Filtrar..." },
      { 
        title: 'Perfil', 
        field: 'id_perfil', 
        minWidth: 150, 
        formatter: (cell) => {
          const nomePerfil = profiles.find(p => p.id_perfil === cell.getValue())?.nome_perfil || "N/A";
          return `<span style="color: #007bff; text-decoration: underline; cursor: pointer;" title="Clique para editar">${nomePerfil}</span>`;
        },
        cellClick: (e, cell) => {
          const user = cell.getRow().getData();
          selecionarUsuario(user);
          setShowUpdateModal(true);
        }
      },
      { title: 'Abrangência', field: 'abrangencia', minWidth: 180, formatter: (cell) => renderScopeDisplay(cell.getRow().getData()) },
      { title: 'Modo consulta', field: 'modo_consulta', width: 170, hozAlign: 'center', formatter: 'tickCross', editor: true, headerSort: false },
      { title: 'Acessa o sistema', field: 'acessa_sistema', width: 190, hozAlign: 'center', formatter: 'tickCross', editor: true, headerSort: false },
      { title: 'Cria questionário', field: 'cria_questionario', width: 200, hozAlign: 'center', formatter: 'tickCross', editor: true, headerSort: false },
      { title: 'Acessa questionário (resultados)', field: 'acessa_resultados', width: 320, hozAlign: 'center', formatter: 'tickCross', editor: true, headerSort: false },
      { title: 'Acesso BDados', field: 'acessa_bdados', width: 180, hozAlign: 'center', formatter: 'tickCross', editor: true, headerSort: false },
      { title: 'Edita tabela tipo', field: 'edita_tabela_tipo', width: 180, hozAlign: 'center', formatter: 'tickCross', editor: true, headerSort: false },
      { title: 'Cria ações', field: 'cria_acoes', width: 150, hozAlign: 'center', formatter: 'tickCross', editor: true, headerSort: false },
      { 
        title: 'UF', 
        field: 'uf',
        width: 80, 
        hozAlign: 'center',
        formatter: (cell) => cell.getValue() || '-'
      },
      { 
        title: 'IBGE7', 
        field: 'codigo_ibge7',
        width: 120, 
        hozAlign: 'center',
        formatter: (cell) => {
          const value = cell.getValue();
          if (!value) return '-';
          // Para municípios (código com 7 dígitos), mostrar apenas os 2 primeiros (código do estado)
          if (value.toString().length === 7) {
            return value.toString().substring(0, 2);
          }
          return value;
        }
      },
      { 
        title: 'INEP', 
        field: 'codigo_inep',
        width: 120, 
        hozAlign: 'center',
        formatter: (cell) => cell.getValue() || '-'
      },
      { 
        title: 'ID Regional', 
        field: 'id_regional',
        width: 120, 
        hozAlign: 'center',
        formatter: (cell) => cell.getValue() || '-'
      },
    ];
  }, [handleApprove, handleReject, profiles, renderScopeDisplay, selecionarUsuario]);

  // 1. Inicializa o Tabulator apenas UMA VEZ quando o loading terminar e limpa no unmount (React 18 Strict Mode)
  useEffect(() => {
    if (loading || !tableRef.current) return;

    const tabulator = new Tabulator(tableRef.current, {
      data: pendingUsers,
      columns: getColunas,
      layout: "fitData",
      pagination: "local",
      paginationSize: 10,
      paginationCounter: "rows",
      movableColumns: true,
      resizableRows: true,
      resizableColumns: true,
      placeholder: "Nenhum usuário pendente de aprovação.",
      locale: "pt-br",
      langs: {
        "pt-br": {
          "pagination": {
            "first": "Primeira",
            "first_title": "Primeira Página",
            "last": "Última",
            "last_title": "Última Página",
            "prev": "Anterior",
            "prev_title": "Página Anterior",
            "next": "Próxima",
            "next_title": "Próxima Página",
            "page_size": "Registros por página",
          },
          "headerFilters": {
            "default": "Filtrar...",
          }
        }
      }
    });

    tabulator.on("tableBuilt", () => {
      setIsTableBuilt(true);
    });

    tabulatorRef.current = tabulator;

    return () => {
      setIsTableBuilt(false);
      if (tabulator) {
        try {
          tabulator.destroy();
        } catch (e) {
          // Ignore
        }
      }
      tabulatorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // 2. Atualiza os dados de forma fluida garantindo que a tabela terminou de renderizar internamente
  useEffect(() => {
    if (isTableBuilt && tabulatorRef.current) {
      try {
        const result = tabulatorRef.current.replaceData(pendingUsers);
        if (result && typeof result.catch === 'function') {
          result.catch(() => {});
        }
      } catch (e) {
        console.error("Erro ao dar replaceData:", e);
      }
    }
  }, [pendingUsers, isTableBuilt]);

  // 3. Atualiza as colunas de forma fluida
  useEffect(() => {
    if (isTableBuilt && tabulatorRef.current) {
      try {
        const result = tabulatorRef.current.setColumns(getColunas);
        if (result && typeof result.catch === 'function') {
          result.catch(() => {});
        }
      } catch (e) {
        console.error("Erro ao setColumns:", e);
      }
    }
  }, [getColunas, isTableBuilt]);

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download("csv", "usuarios-pendentes.csv");
    }
  };

  const handleNomeBuscaChange = (value) => {
    setNomeBusca(value);
    
    if (!value.trim()) {
      setUsuariosFiltrados([]);
      return;
    }
    
    const filtrados = pendingUsers.filter(usuario => 
      usuario.nome_completo?.toLowerCase().includes(value.toLowerCase()) ||
      usuario.email?.toLowerCase().includes(value.toLowerCase())
    );
    setUsuariosFiltrados(filtrados);
  };



  useEffect(() => {
    if (novoUf) {
      const fetchMunicipiosUpdate = async () => {
        try {
          const response = await api.get(`/public/municipios/by-uf/${novoUf}`);
          setMunicipiosFiltradosUpdate(response.data.data || []);
        } catch (error) {
          console.error('Erro ao buscar municípios:', error);
        }
      };
      fetchMunicipiosUpdate();
    }
  }, [novoUf]);

  useEffect(() => {
    if (novoIdMunicipio) {
      const fetchEscolasUpdate = async () => {
        try {
          const response = await api.get(`/public/escolas/by-municipio/${novoIdMunicipio}`);
          setEscolasFiltradas(response.data.data || []);
        } catch (error) {
          console.error('Erro ao buscar escolas:', error);
        }
      };
      fetchEscolasUpdate();
    }
  }, [novoIdMunicipio]);

  const atualizarPerfilUsuario = async () => {
    if (!usuarioEncontrado) {
      mostrarMensagem('error', 'Nenhum usuário selecionado.');
      return;
    }
    
    if (!novoPerfilId) {
      mostrarMensagem('error', 'Por favor, selecione um perfil.');
      return;
    }

    try {
      const payload = {
        id_perfil: parseInt(novoPerfilId)
      };

      // Adicionar campos apenas se estiverem preenchidos
      if (novoUf) payload.uf = novoUf;
      if (novoIdMunicipio) payload.id_municipio = parseInt(novoIdMunicipio);
      if (novoIdRegional) payload.id_regional = parseInt(novoIdRegional);
      if (novoIdEscola) payload.codigo_inep = novoIdEscola;

      console.log('Enviando payload:', payload);
      await api.put(`/usuario/${usuarioEncontrado.id_usuario}`, payload);
      
      // Fechar modal primeiro
      fecharModalUpdate();
      
      // Destruir a tabela atual para reconstruir com todas as colunas
      if (tabulatorRef.current) {
        try {
          tabulatorRef.current.destroy();
          tabulatorRef.current = null;
        } catch (e) {
          console.error('Erro ao destruir tabela:', e);
        }
      }
      
      // Recarregar os dados - a tabela será completamente reconstruída pelo useEffect
      await fetchData(false);
      
      // Mostrar mensagem de sucesso
      mostrarMensagem('success', 'Perfil atualizado com sucesso!');
    } catch (err) {
      mostrarMensagem('error', err.response?.data?.message || 'Erro ao atualizar perfil.');
      console.error('Erro completo:', err.response?.data);
    }
  };

  const fecharModalUpdate = () => {
    setShowUpdateModal(false);
    setNomeBusca('');
    setUsuarioEncontrado(null);
    setUsuariosFiltrados([]);
    setNovoPerfilId('');
    setNovoUf('');
    setNovoIdMunicipio('');
    setNovoIdRegional('');
    setNovoIdEscola('');
    setMunicipioSearchUpdate('');
    setEscolaSearch('');
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>
      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Aprovação de novos usuários</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Gerencie os novos usuários pendentes de aprovação no sistema.</p>
      </div>

      {message.texto && (<div className="status-container"><div className={`status ${message.tipo}`}>{message.texto}</div></div>)}

      <div className="controls">
          <button className="btn btn-primary" onClick={() => fetchData(true)} disabled={loading}>
            {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
          </button>
          <button className="btn btn-secondary" onClick={exportarDados} disabled={pendingUsers.length === 0}>📥 Exportar CSV</button>
          <button className="btn btn-warning" onClick={() => setShowUpdateModal(true)}>✏️ Atualizar Perfil</button>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{pendingUsers.length}</strong> usuários pendentes</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        {loading ? <p>Carregando...</p> : <div ref={tableRef}></div>}
      </div>

      {/* Modal de Atualização de Perfil */}
      {showUpdateModal && (
        <div className="modal-overlay" onClick={fecharModalUpdate}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Atualizar Perfil de Usuário</h2>
              <button className="modal-close" onClick={fecharModalUpdate}>×</button>
            </div>
            <div className="modal-body">
              {!usuarioEncontrado ? (
                <>
                  <div className="form-group">
                    <label>Nome do Usuário</label>
                    <input
                      type="text"
                      placeholder="Digite o nome do usuário..."
                      value={nomeBusca}
                      onChange={(e) => handleNomeBuscaChange(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  
                  {usuariosFiltrados.length > 0 && nomeBusca && (
                    <div style={{ 
                      maxHeight: '300px', 
                      overflowY: 'auto', 
                      border: '1px solid #dee2e6', 
                      borderRadius: '4px',
                      marginTop: '10px'
                    }}>
                      {usuariosFiltrados.slice(0, 20).map((usuario) => (
                        <div 
                          key={usuario.id_usuario}
                          onClick={() => selecionarUsuario(usuario)}
                          style={{
                            padding: '12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <div style={{ fontWeight: '600', color: '#2c3e50' }}>{usuario.nome_completo}</div>
                          <div style={{ fontSize: '13px', color: '#6c757d' }}>
                            {usuario.email} • CPF: {formatCpf(usuario.cpf)}
                          </div>
                          <div style={{ fontSize: '12px', color: '#95a5a6' }}>
                            {profiles.find(p => p.id_perfil === usuario.id_perfil)?.nome_perfil || 'N/A'}
                          </div>
                        </div>
                      ))}
                      {usuariosFiltrados.length > 20 && (
                        <div style={{ padding: '10px', textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>
                          Mostrando 20 de {usuariosFiltrados.length} resultados. Continue digitando para refinar.
                        </div>
                      )}
                    </div>
                  )}
                  
                  {usuariosFiltrados.length === 0 && nomeBusca && (
                    <p style={{ color: '#dc3545', marginTop: '10px' }}>Nenhum usuário pendente encontrado.</p>
                  )}
                </>
              ) : (
                <>
                  <div className="user-info" style={{ padding: '15px', background: '#f0f0f0', borderRadius: '5px', marginBottom: '20px' }}>
                    <h3>Usuário Encontrado:</h3>
                    <p><strong>Nome:</strong> {usuarioEncontrado.nome_completo}</p>
                    <p><strong>Email:</strong> {usuarioEncontrado.email}</p>
                    <p><strong>CPF:</strong> {formatCpf(usuarioEncontrado.cpf)}</p>
                  </div>

                  <div className="form-group">
                    <label>Novo Perfil</label>
                    <select value={novoPerfilId} onChange={(e) => setNovoPerfilId(e.target.value)}>
                      <option value="">Selecione um perfil</option>
                      {profiles.map((perfil) => (
                        <option key={perfil.id_perfil} value={perfil.id_perfil}>{perfil.nome_perfil}</option>
                      ))}
                    </select>
                  </div>

                  {(novoPerfilId === '2' || novoPerfilId === '3' || novoPerfilId === '4' || novoPerfilId === '5') && (
                    <div className="form-group">
                      <label>Estado</label>
                      <select value={novoUf} onChange={(e) => setNovoUf(e.target.value)}>
                        <option value="">Selecione um estado</option>
                        {estados.map((estado) => (
                          <option key={estado.id_sigla_est} value={estado.id_sigla_est}>{estado.nome_estado}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {novoPerfilId === '5' && novoUf && (
                    <div className="form-group">
                      <label>Regional</label>
                      <select value={novoIdRegional} onChange={(e) => setNovoIdRegional(e.target.value)}>
                        <option value="">Selecione uma regional</option>
                        {regionais.filter(r => r.uf === novoUf).map((regional) => (
                          <option key={regional.id_regional} value={regional.id_regional}>{regional.nome_regional}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {(novoPerfilId === '3' || novoPerfilId === '4') && novoUf && (
                    <div className="form-group">
                      <label>Município</label>
                      <input 
                        type="text"
                        value={municipioSearchUpdate}
                        onChange={(e) => {
                          setMunicipioSearchUpdate(e.target.value);
                          const municipioEncontrado = municipiosFiltradosUpdate.find(mun => 
                            mun.nome_municipio === e.target.value
                          );
                          if (municipioEncontrado) {
                            setNovoIdMunicipio(municipioEncontrado.id_municipio);
                          } else {
                            setNovoIdMunicipio('');
                          }
                        }}
                        list="municipios-update-list"
                        placeholder="Digite para buscar município..."
                        autoComplete="off"
                      />
                      <datalist id="municipios-update-list">
                        {municipiosFiltradosUpdate.map((municipio) => (
                          <option key={municipio.id_municipio} value={municipio.nome_municipio} />
                        ))}
                      </datalist>
                    </div>
                  )}

                  {novoPerfilId === '4' && novoIdMunicipio && (
                    <div className="form-group">
                      <label>Escola</label>
                      <input 
                        type="text"
                        value={escolaSearch}
                        onChange={(e) => {
                          setEscolaSearch(e.target.value);
                          const escolaEncontrada = escolasFiltradas.find(esc => 
                            esc.nome_escola === e.target.value
                          );
                          if (escolaEncontrada) {
                            setNovoIdEscola(escolaEncontrada.codigo_inep);
                          } else {
                            setNovoIdEscola('');
                          }
                        }}
                        list="escolas-update-list"
                        placeholder="Digite para buscar escola..."
                        autoComplete="off"
                      />
                      <datalist id="escolas-update-list">
                        {escolasFiltradas.map((escola) => (
                          <option key={escola.codigo_inep} value={escola.nome_escola} />
                        ))}
                      </datalist>
                    </div>
                  )}



                  <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button className="btn btn-success" onClick={atualizarPerfilUsuario}>
                      ✅ Salvar Alterações
                    </button>
                    <button className="btn btn-secondary" onClick={() => { setUsuarioEncontrado(null); setNomeBusca(''); }}>
                      ← Buscar Outro Usuário
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Aprovação com Permissões */}
      {showApproveModal && usuarioParaAprovar && (
        <div className="modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Aprovar Usuário</h2>
              <button className="modal-close" onClick={() => setShowApproveModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-info" style={{ padding: '15px', background: '#f0f0f0', borderRadius: '5px', marginBottom: '20px' }}>
                <h3>Usuário Selecionado:</h3>
                <p><strong>Nome:</strong> {usuarioParaAprovar.nome_completo}</p>
                <p><strong>Email:</strong> {usuarioParaAprovar.email}</p>
                <p><strong>CPF:</strong> {formatCpf(usuarioParaAprovar.cpf)}</p>
                <p><strong>Perfil:</strong> {profiles.find(p => p.id_perfil === usuarioParaAprovar.id_perfil)?.nome_perfil || 'N/A'}</p>
              </div>

              <h3 style={{ marginBottom: '15px' }}>Permissões do Usuário:</h3>

              <div style={{ padding: '15px', background: '#f9f9f9', borderRadius: '5px', marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: '5px 0' }}>
                  <input type="checkbox" checked={aprovacaoModoConsulta} onChange={(e) => setAprovacaoModoConsulta(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                  <span><strong>Modo Consulta (Apenas Leitura)</strong></span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: '5px 0' }}>
                  <input type="checkbox" checked={aprovacaoAcessoSistema} onChange={(e) => setAprovacaoAcessoSistema(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                  <span><strong>Acessa o sistema</strong></span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: '5px 0' }}>
                  <input type="checkbox" checked={aprovacaoCriarQuestionarios} onChange={(e) => setAprovacaoCriarQuestionarios(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                  <span><strong>Cria questionário</strong></span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: '5px 0' }}>
                  <input type="checkbox" checked={aprovacaoAcessoResultados} onChange={(e) => setAprovacaoAcessoResultados(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                  <span><strong>Acessa questionário (resultados)</strong></span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: '5px 0' }}>
                  <input type="checkbox" checked={aprovacaoAcessoBDados} onChange={(e) => setAprovacaoAcessoBDados(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                  <span><strong>Acesso BDados</strong></span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: '5px 0' }}>
                  <input type="checkbox" checked={aprovacaoEditarTabelas} onChange={(e) => setAprovacaoEditarTabelas(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                  <span><strong>Edita tabela tipo</strong></span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: '5px 0' }}>
                  <input type="checkbox" checked={aprovacaoCriaAcoes} onChange={(e) => setAprovacaoCriaAcoes(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                  <span><strong>Cria ações</strong></span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-success" onClick={confirmarAprovacao}>
                  ✅ Aprovar Usuário
                </button>
                <button className="btn btn-secondary" onClick={() => setShowApproveModal(false)}>
                  ✕ Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciadorAprovacoes;
